import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { homePathForUser, useAuth } from "../auth/AuthContext";
import { api, ApiError } from "../lib/api";
import { validateNewPassword } from "../lib/password";

import { torneos123HeaderLogoSrc } from "../lib/brandLogo";

// Una sola página para tres usos vía dos modos: "self" cubre tanto el cambio
// self-service como el forzado tras login con temporal (el gate de RequireRole
// redirige aquí y la bandera must_change_password del user decide el copy y si
// se pide la contraseña actual); "reset" es la confirmación pública de
// recuperación vía token. Los endpoints de backend (change-password y
// password-reset/confirm) devuelven un access_token fresco porque cambiar la
// contraseña revoca el token con el que se llamó — por eso siempre se adopta
// sesión nueva en lugar de solo actualizar el user en memoria.
export default function CreatePasswordPage({ mode }: { mode: "self" | "reset" }) {
  const { token, user, adoptSession } = useAuth();
  const { resetToken } = useParams();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showRecoverLink, setShowRecoverLink] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isForcedChange = mode === "self" && Boolean(user?.must_change_password);
  const needsCurrent = mode === "self" && !isForcedChange;
  const title = mode === "reset" || isForcedChange ? "Crea tu contraseña" : "Cambiar contraseña";

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validation = validateNewPassword(password, confirmation);
    if (validation) {
      setError(validation);
      return;
    }
    setError(null);
    setShowRecoverLink(false);
    setSubmitting(true);
    // Capturar ANTES del submit: adoptSession apaga la bandera y en la PWA
    // instalada (historial de una entrada) navigate(-1) sería un no-op — el
    // usuario forzado quedaría varado viendo aparecer "Contraseña actual".
    const wasForced = isForcedChange;
    try {
      if (mode === "reset") {
        const response = await api.auth.passwordResetConfirm(resetToken ?? "", password.trim());
        adoptSession(response.data);
        navigate(homePathForUser(response.data.user), { replace: true });
      } else {
        if (!token) return;
        const response = await api.auth.changePassword(token, {
          current_password: needsCurrent ? currentPassword : null,
          new_password: password.trim(),
        });
        adoptSession(response.data);
        if (wasForced) {
          navigate(homePathForUser(response.data.user), { replace: true });
        } else {
          navigate(-1);
        }
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        if (mode === "reset") {
          setError("El enlace ya no es válido o expiró. Pide uno nuevo desde");
          setShowRecoverLink(true);
        } else {
          setError("La contraseña actual no es correcta.");
        }
      } else {
        setError("No pudimos guardar la contraseña. Intenta otra vez.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-shell">
      <header className="login-shell__brand">
        <img className="login-shell__logo" src={torneos123HeaderLogoSrc()} alt="Torneos123" />
        <p className="login-shell__eyebrow">Compite · Progresa · Gana</p>
        <p className="login-shell__subtitle">{title}</p>
      </header>

      <section className="login-shell__card">
        {isForcedChange && (
          <p className="login-shell__helper" style={{ margin: "0 0 0.9rem" }}>
            Por seguridad, crea tu propia contraseña antes de continuar.
          </p>
        )}
        <form className="form" onSubmit={handleSubmit}>
          {needsCurrent && (
            <label>
              Contraseña actual
              <input
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                disabled={submitting}
              />
            </label>
          )}
          <label>
            Nueva contraseña (mínimo 8 caracteres)
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={submitting}
            />
          </label>
          <label>
            Confirma tu nueva contraseña
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              disabled={submitting}
            />
          </label>
          {error && (
            <p role="alert" className="form-error">
              {error}
              {showRecoverLink && (
                <>
                  {" "}
                  <Link to="/recuperar">Recuperar contraseña</Link>.
                </>
              )}
            </p>
          )}
          <button className="btn" type="submit" disabled={submitting}>
            {submitting ? "Guardando…" : "Guardar contraseña"}
          </button>
        </form>
      </section>

      <p className="login-shell__credit">Torneos123 · Sistema de torneos</p>
    </div>
  );
}
